import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useLanguage } from '../../utils/LanguageContext';
import { useCart } from '../../utils/CartContext'; // ADDED THIS LINE
import { getLocalized } from '../../utils/languageUtils';
import { useAuth } from '../../utils/useAuth';
import '../../styles/shop.css';

const API_URL = 'products/';

// Sticker for each tree family name returned by the backend.
const FAMILY_STICKERS = {
    'Mevali daraxtlar': 'cat-fruit',
    'Manzarali daraxtlar': 'cat-decorative',
    'Manzarali gullar': 'cat-flowers',
    'Butalar': 'cat-bushes',
};
const familySticker = (name) => `/images/stickers/${FAMILY_STICKERS[name] || 'sapling-pin'}.webp`;

export default function Shop() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [addedItems, setAddedItems] = useState({});
    const { t, language } = useLanguage();
    const { addToCart } = useCart();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [priceFilter, setPriceFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name-asc');

    useEffect(() => {
        setLoading(true);
        setError(null);
        const params = {};

        if (searchQuery.trim()) {
            params.search = searchQuery;
        }

        if (categoryFilter !== 'all') {
            params['tree__family__name'] = categoryFilter;
        }

        if (priceFilter === 'under-80') {
            params.price__gte = 0;
            params.price__lt = 80000;
        } else if (priceFilter === '80-110') {
            params.price__gte = 80000;
            params.price__lt = 110000;
        } else if (priceFilter === 'over-110') {
            params.price__gte = 110000;
        }

        const orderingMap = {
            'name-asc': `tree__name_${language}`,
            'name-desc': `-tree__name_${language}`,
            'price-asc': 'price',
            'price-desc': '-price',
        };
        params.ordering = orderingMap[sortBy];

        api.get(API_URL, { params })
            .then(res => { setProducts(res.data); setError(null); })
            .catch(err => { console.error(err); setError(t('shop_error')); setProducts([]); })
            .finally(() => setLoading(false));
    }, [searchQuery, categoryFilter, priceFilter, sortBy, language, t]);

    // UPDATED THIS FUNCTION TO USE CART CONTEXT
    const handleAddToCart = async (e, product) => {
        e.preventDefault();

        if (!isAuthenticated) {
            navigate('/auth?mode=login');
            return;
        }

        const success = await addToCart(product.id, 1);
        
        if (success) {
            setAddedItems(prev => ({ ...prev, [product.id]: true }));
            setTimeout(() => {
                setAddedItems(prev => ({ ...prev, [product.id]: false }));
            }, 2000);
        }
    };

    const clearFilters = () => {
        setSearchQuery(''); 
        setCategoryFilter('all'); 
        setPriceFilter('all'); 
        setSortBy('name-asc');
    };

    const hasActiveFilters = searchQuery || categoryFilter !== 'all' || priceFilter !== 'all' || sortBy !== 'name-asc';

    // Smart Sticky Sidebar Logic
    useEffect(() => {
        const handleScroll = () => {
            const sidebar = document.querySelector('.filters-sidebar');
            const container = document.querySelector('.shop-container');
            
            if (!sidebar || !container) return;

            const containerRect = container.getBoundingClientRect();
            const sidebarHeight = sidebar.offsetHeight;
            const windowHeight = window.innerHeight;
            const navbarHeight = 100; // Adjust based on your navbar height

            // Calculate positions
            const containerTop = containerRect.top;
            const containerBottom = containerRect.bottom;
            const scrollTrigger = navbarHeight;

            // Remove all classes first
            sidebar.classList.remove('is-sticky', 'is-bottom');

            // If sidebar is taller than viewport, use simple sticky
            if (sidebarHeight > windowHeight - navbarHeight) {
                if (containerTop <= scrollTrigger && containerBottom > sidebarHeight + scrollTrigger) {
                    sidebar.classList.add('is-sticky');
                }
                return;
            }

            // Smart sticky behavior
            if (containerTop <= scrollTrigger) {
                // Container has scrolled past navbar
                if (containerBottom >= sidebarHeight + scrollTrigger + 40) {
                    // Enough space to stick
                    sidebar.classList.add('is-sticky');
                } else {
                    // Bottom of container reached, stick to bottom
                    sidebar.classList.add('is-bottom');
                }
            }
            // else: sidebar scrolls naturally with container
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Run on mount

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="shop-page">
            {/* Hero Section */}
            <section className="shop-hero">
                <div className="shop-hero-bg" aria-hidden="true" />
                <div className="shop-hero-content">
                    <div className="shop-hero-badge hero-enter" style={{ '--d': '0.05s' }}>
                        <i className="fas fa-leaf"></i>
                        <span>{t('shop_hero_badge')}</span>
                    </div>
                    <h1 className="hero-enter" style={{ '--d': '0.15s' }}>
                        {t('shop_hero_title')} <span className="highlight serif-accent">{t('shop_hero_highlight')}</span>
                    </h1>
                    <p className="hero-enter" style={{ '--d': '0.3s' }}>{t('shop_hero_desc')}</p>
                </div>
            </section>

            {/* Main Shop Layout: LEFT Sidebar + RIGHT Products */}
            <div className="shop-container">
                {/* LEFT SIDEBAR - FILTERS */}
                <aside className="filters-sidebar">
                    <h2 className="filters-sidebar-title">
                        <span className="filters-title-icon"><i className="fas fa-sliders"></i></span>
                        {t('shop_filters_title')}
                    </h2>

                    {/* Search */}
                    <div className="sidebar-search">
                        <i className="fas fa-magnifying-glass sidebar-search-icon"></i>
                        <input 
                            type="text" 
                            placeholder={t('shop_search_placeholder')} 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                            className="sidebar-search-input" 
                        />
                        {searchQuery && (
                            <button className="sidebar-clear-search" onClick={() => setSearchQuery('')}>×</button>
                        )}
                    </div>

                    {/* Category Filter */}
                    <div className="sidebar-filter-group">
                        <label className="sidebar-filter-label">{t('shop_filter_cat')}</label>
                        <div className="sidebar-filter-options">
                            <button
                                className={`sidebar-filter-chip ${categoryFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setCategoryFilter('all')}
                            >
                                {t('shop_cat_all')}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${categoryFilter === 'Mevali daraxtlar' ? 'active' : ''}`}
                                onClick={() => setCategoryFilter('Mevali daraxtlar')}
                            >
                                <img className="chip-sticker" src={familySticker('Mevali daraxtlar')} alt="" />
                                {t('filter_fruit')}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${categoryFilter === 'Manzarali daraxtlar' ? 'active' : ''}`}
                                onClick={() => setCategoryFilter('Manzarali daraxtlar')}
                            >
                                <img className="chip-sticker" src={familySticker('Manzarali daraxtlar')} alt="" />
                                {t('filter_decorative')}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${categoryFilter === 'Manzarali gullar' ? 'active' : ''}`}
                                onClick={() => setCategoryFilter('Manzarali gullar')}
                            >
                                <img className="chip-sticker" src={familySticker('Manzarali gullar')} alt="" />
                                {t('map_cat_flowers')}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${categoryFilter === 'Butalar' ? 'active' : ''}`}
                                onClick={() => setCategoryFilter('Butalar')}
                            >
                                <img className="chip-sticker" src={familySticker('Butalar')} alt="" />
                                {t('map_cat_bushes')}
                            </button>
                        </div>
                    </div>

                    {/* Price Filter */}
                    <div className="sidebar-filter-group">
                        <label className="sidebar-filter-label">{t('shop_filter_price')}</label>
                        <div className="sidebar-filter-options">
                            <button
                                className={`sidebar-filter-chip ${priceFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setPriceFilter('all')}
                            >
                                {t('shop_price_all')}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${priceFilter === 'under-80' ? 'active' : ''}`}
                                onClick={() => setPriceFilter('under-80')}
                            >
                                {t('shop_price_under_80') || t('shop_price_under_5') /* Fallback if keys mismatched across langs */}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${priceFilter === '80-110' ? 'active' : ''}`}
                                onClick={() => setPriceFilter('80-110')}
                            >
                                {t('shop_price_80_110') || t('shop_price_5_10')}
                            </button>
                            <button
                                className={`sidebar-filter-chip ${priceFilter === 'over-110' ? 'active' : ''}`}
                                onClick={() => setPriceFilter('over-110')}
                            >
                                {t('shop_price_over_110') || t('shop_price_over_20')}
                            </button>
                        </div>
                    </div>

                    {/* Sort */}
                    <div className="sidebar-filter-group">
                        <label className="sidebar-filter-label">{t('shop_sort_label')}</label>
                        <select 
                            className="sidebar-sort-select" 
                            value={sortBy} 
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name-asc">{t('shop_sort_name_asc')}</option>
                            <option value="name-desc">{t('shop_sort_name_desc')}</option>
                            <option value="price-asc">{t('shop_sort_price_asc')}</option>
                            <option value="price-desc">{t('shop_sort_price_desc')}</option>
                        </select>
                    </div>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <button className="sidebar-clear-btn" onClick={clearFilters}>
                            {t('shop_clear_filters')}
                        </button>
                    )}
                </aside>

                {/* RIGHT AREA - PRODUCTS */}
                <main className="products-area">
                    {/* Products Header */}
                    <div className="products-header">
                        <div className="products-count">
                            {!loading && <><strong>{products.length}</strong> {t('shop_found')}</>}
                        </div>
                    </div>

                    {/* Error */}
                    {error && <div className="error-banner"><i className="fas fa-triangle-exclamation"></i> {error}</div>}

                    {/* Loading */}
                    {loading && (
                        <div className="products-grid" aria-busy="true" aria-label={t('shop_loading')}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="product-card skeleton-card" data-no-reveal>
                                    <div className="skeleton skeleton-image" />
                                    <div className="skeleton-body">
                                        <div className="skeleton skeleton-line short" />
                                        <div className="skeleton skeleton-line" />
                                        <div className="skeleton skeleton-line" />
                                        <div className="skeleton skeleton-btn" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Products Grid */}
                    {!loading && !error && (
                        <div className="products-grid">
                            {products.map(product => (
                                <div key={product.id} className="product-card">
                                    <Link to={`/product/${product.id}`} className="product-card-link">
                                        <div className="product-image">
                                            <img 
                                                src={product.tree?.image} 
                                                alt={getLocalized(product.tree, language, 'name') || product.tree?.name_en || ''} 
                                                loading="lazy"
                                            />
                                            <span className="product-image-cta">
                                                <i className="fas fa-arrow-up-right-from-square"></i>
                                            </span>
                                        </div>
                                        <div className="product-info">
                                            <div className="product-category">
                                                <img className="product-category-sticker" src={familySticker(product.tree?.family?.name)} alt="" />
                                                {product.tree?.family?.name === 'Mevali daraxtlar' && t('filter_fruit')}
                                                {product.tree?.family?.name === 'Manzarali daraxtlar' && t('filter_decorative')}
                                                {product.tree?.family?.name === 'Manzarali gullar' && t('map_cat_flowers')}
                                                {product.tree?.family?.name === 'Butalar' && t('map_cat_bushes')}
                                                {!['Mevali daraxtlar', 'Manzarali daraxtlar', 'Manzarali gullar', 'Butalar'].includes(product.tree?.family?.name) && (product.tree?.family?.name || 'Tree')}
                                            </div>
                                            <h3 className="product-name">
                                                {getLocalized(product.tree, language, 'name') || 'Unknown Tree'}
                                            </h3>
                                            <p className="product-desc">
                                                {(() => {
                                                    const desc = getLocalized(product, language, 'desc') || '';
                                                    return desc.length > 90 ? `${desc.slice(0, 90).trimEnd()}…` : desc;
                                                })()}
                                            </p>
                                        </div>
                                    </Link>
                                    <div className="product-footer-with-buttons">
                                        <div className="product-price">
                                            {Number(product.price).toLocaleString()} 
                                            <span className="price-currency">UZS</span>
                                        </div>
                                        <div className="product-action-buttons">
                                            <button 
                                                className={`btn-add-cart ${addedItems[product.id] ? 'added' : ''}`} 
                                                onClick={(e) => handleAddToCart(e, product)}
                                            >
                                                <i className={`fas ${addedItems[product.id] ? 'fa-check' : 'fa-cart-plus'}`}></i>
                                                <span>{addedItems[product.id] ? t('shop_btn_added') : t('shop_btn_add')}</span>
                                            </button>
                                            <Link to={`/product/${product.id}`} className="btn-view-details">
                                                {t('shop_btn_details')}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && !error && products.length === 0 && (
                        <div className="empty-state">
                            <img className="empty-sticker" src="/images/stickers/search.webp" alt="" />
                            <h3>{t('shop_empty_title')}</h3>
                            <p>{t('shop_empty_desc')}</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}